import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { contactAPI, settingsAPI } from '../services/api';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await settingsAPI.get();
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await contactAPI.submit(formData);
      toast.success('Mensaje enviado correctamente. Nos contactaremos pronto.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Error al enviar mensaje. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-stone-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-stone-800 mb-4">
            Contacto
          </h2>
          <div className="h-1 w-24 bg-clay-primary mx-auto mb-6"></div>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto">
            ¿Necesitas una máquina específica o una cotización?
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <h3 className="text-2xl font-bold text-stone-800 mb-6">Envíanos un mensaje</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">
                  Nombre
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Tu nombre completo"
                  className="border-stone-300 focus:border-clay-primary focus:ring-clay-primary"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="tu@email.com"
                  className="border-stone-300 focus:border-clay-primary focus:ring-clay-primary"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-2">
                  Mensaje
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Cuéntanos qué máquina necesitas o qué proyecto tienes en mente..."
                  rows={5}
                  className="border-stone-300 focus:border-clay-primary focus:ring-clay-primary resize-none"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-clay-primary hover:bg-clay-dark text-white py-6 text-lg font-semibold rounded-md shadow-lg hover:shadow-xl transition-all duration-300"
                disabled={loading}
              >
                {loading ? 'Enviando...' : 'Enviar Mensaje'}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-stone-800 mb-6">Información de contacto</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-clay-primary/10 rounded-lg flex items-center justify-center">
                      <Phone className="w-6 h-6 text-clay-primary" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800 mb-1">Teléfono</h4>
                    <p className="text-stone-600">{settings?.phone || '+54 911 6766 7569'}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-clay-primary/10 rounded-lg flex items-center justify-center">
                      <Mail className="w-6 h-6 text-clay-primary" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800 mb-1">Email</h4>
                    <p className="text-stone-600">{settings?.email || 'contacto@hallstatt.com'}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-clay-primary/10 rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-clay-primary" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800 mb-1">Ubicación</h4>
                    <p className="text-stone-600">Buenos Aires, Argentina</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-clay-primary p-8 rounded-lg shadow-lg text-white">
              <h3 className="text-2xl font-bold mb-4">¿Prefieres hablar directamente?</h3>
              <p className="mb-6 text-white/90">
                Contáctanos por WhatsApp para una respuesta inmediata
              </p>
              <Button
                onClick={() => window.open(`https://wa.me/${settings?.whatsapp?.replace(/\D/g, '') || '5491167667569'}`, '_blank')}
                className="w-full bg-white text-clay-primary hover:bg-stone-100 py-6 text-lg font-semibold rounded-md shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Abrir WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
